import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import MessageList from '../components/MessageList';
import ChatWindow from '../components/ChatWindow';
import InstagramConnect from '../components/InstagramConnect';
import { useAuth } from '../hooks/useAuth';
import AuthButton from '../components/AuthButton';
import { useQuery } from 'react-query';
import { getInstagramAccount } from '../lib/instagram';

interface Message {
  id: string;
  sender: string;
  content: string;
  timestamp: string;
  isRead: boolean;
  isInfluencer?: boolean;
  avatar?: string;
}

export default function Inbox() {
  const { username } = useParams();
  const { user } = useAuth();
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const { data: account } = useQuery('instagramAccount', getInstagramAccount);

  const messages: Message[] = username ? [
    {
      id: username,
      sender: username,
      content: 'New conversation',
      timestamp: new Date().toISOString(),
      isRead: true,
      isInfluencer: true
    }
  ] : [
    {
      id: '1',
      sender: 'sarahparker.style',
      content: 'Love your latest collection! Can we discuss a potential collab?',
      timestamp: new Date().toISOString(),
      isRead: false,
      avatar: 'https://i.pravatar.cc/150?u=sarahparker.style'
    },
    {
      id: '2',
      sender: 'mike.wellness',
      content: 'Hey! Interested in featuring your products in my next wellness video',
      timestamp: new Date(Date.now() - 3600000).toISOString(),
      isRead: true,
      avatar: 'https://i.pravatar.cc/150?u=mike.wellness'
    }
  ];

  const handleMessageSelect = (messageId: string) => {
    setSelectedMessageId(messageId);
  };

  if (!user) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold text-gray-900">Social Media Inbox</h1>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <h2 className="mt-4 text-lg font-medium text-gray-900">
              Sign In to Get Started
            </h2>
            <p className="mt-2 text-gray-600">
              Sign in to connect your Instagram account and manage your messages.
            </p>
            <div className="mt-6">
              <AuthButton />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const selectedMessage = messages.find(m => m.id === (username || selectedMessageId));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-gray-900">Social Media Inbox</h1>
        <InstagramConnect />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Message list */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <MessageList
            messages={messages}
            selectedMessageId={selectedMessageId}
            onMessageSelect={handleMessageSelect}
          />
        </div>

        {/* Chat window */}
        <div className="lg:col-span-2">
          {selectedMessageId || username ? (
            <ChatWindow 
              messages={[]} 
              username={username || selectedMessage?.sender || ''} 
              isDisabled={!account?.connected}
              isInfluencer={selectedMessage?.isInfluencer}
              avatar={selectedMessage?.avatar}
            />
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
              <div className="flex flex-col items-center justify-center h-full text-center">
                <svg
                  className="h-12 w-12 text-gray-400 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900">Select a conversation</h3>
                <p className="text-gray-500 mt-1">
                  Choose a conversation from the list to start messaging
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}